import { SourceLinks } from '../ui/source-links';
import { Sparkles, Building2 } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useState } from 'react';
import { DiscoveryCard } from './discovery-card';

interface SocialWorldCardProps {
    socialWorld: CultureByBook['socialWorld'];
    isExpanded?: boolean;
    onToggleExpansion?: () => void;
    defaultExpanded?: boolean;
}

/**
 * Shared component for displaying social world data.
 * Used in both the cultural context page and the capability modal.
 */
export function SocialWorldCard({
    socialWorld,
    isExpanded: controlledIsExpanded,
    onToggleExpansion: controlledOnToggleExpansion,
    defaultExpanded = false
}: SocialWorldCardProps) {
    const isDarkMode = useDarkMode();
    const [internalIsExpanded, setInternalIsExpanded] = useState(defaultExpanded);

    if (!socialWorld) {
        return null;
    }

    const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;
    const onToggleExpansion = controlledOnToggleExpansion !== undefined
        ? controlledOnToggleExpansion
        : () => setInternalIsExpanded(!internalIsExpanded);

    const Section = ({ title, content, sources }: { title: string; content?: string; sources?: Source[] }) => {
        if (!content) return null;
        return (
            <div className="space-y-1.5 border-l border-primary/10 pl-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 flex items-center gap-2">
                    <Sparkles className="h-2.5 w-2.5" />
                    {title}
                </div>
                <p className="text-sm leading-relaxed text-foreground/70 font-light italic font-serif">
                    {content}
                </p>
                {sources?.length > 0 && (
                    <div className="pt-2">
                        <SourceLinks sources={sources} className="mt-1" />
                    </div>
                )}
            </div>
        );
    };

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            title="Social World"
            icon={Building2}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <Section
                    title="Patronage System"
                    content={socialWorld.patronageSystem?.description}
                    sources={socialWorld.patronageSystem?.sources}
                />
                <Section
                    title="Honor & Shame"
                    content={socialWorld.honorShame?.description}
                    sources={socialWorld.honorShame?.sources}
                />
                <Section
                    title="Gender Roles"
                    content={socialWorld.genderRoles?.description}
                    sources={socialWorld.genderRoles?.sources}
                />
                <Section
                    title="Family Structure"
                    content={socialWorld.familyStructure?.description}
                    sources={socialWorld.familyStructure?.sources}
                />
            </div>
        </DiscoveryCard>
    );
}
